import React, { useEffect, useState } from 'react';
import {
  Card,
  Title,
  Text,
  Table,
  TableHead,
  TableHeaderCell,
  TableBody,
  TableRow,
  TableCell,
  Badge,
} from '@tremor/react';
import Header from '../components/layout/Header';
import { patientsApi, Patient, PatientExtraDetails } from '../services/api';

// Every Patient field except the ones already shown in the compact row —
// rendered as a label/value grid when a row is expanded. Order roughly
// mirrors the BigQuery view's column order.
const DETAIL_FIELDS: { key: keyof Patient; label: string }[] = [
  { key: 'first_name', label: 'First name' },
  { key: 'last_name', label: 'Last name' },
  { key: 'email', label: 'Email' },
  { key: 'age', label: 'Age' },
  { key: 'dob', label: 'DOB' },
  { key: 'gender', label: 'Gender' },
  { key: 'city', label: 'City' },
  { key: 'area', label: 'Area' },
  { key: 'patient_type', label: 'Patient type' },
  { key: 'clinic_id', label: 'Clinic ID' },
  { key: 'clinic_name', label: 'Clinic name' },
  { key: 'clinic_group', label: 'Clinic group' },
  { key: 'sub_clinic', label: 'Sub clinic' },
  { key: 'doctor_id', label: 'Doctor ID' },
  { key: 'psy_name', label: 'Psychiatrist' },
  { key: 'case_manager_id', label: 'Case manager ID' },
  { key: 'cm_name', label: 'Case manager' },
  { key: 'status_group', label: 'Status group' },
  { key: 'source', label: 'Source' },
  { key: 'reason_for_referral', label: 'Reason for referral' },
  { key: 'intake_done', label: 'Intake done' },
  { key: 'active', label: 'Active' },
  { key: 'approved', label: 'Approved' },
  { key: 'red_flag_reason', label: 'Red flag reason' },
  { key: 'start_date', label: 'Start date' },
  { key: 'last_interaction', label: 'Last interaction' },
  { key: 'last_meeting', label: 'Last meeting' },
  { key: 'renew_date', label: 'Renew date' },
  { key: 'theraphy_finish', label: 'Therapy finish' },
  { key: 'finish_reason', label: 'Finish reason' },
  { key: 'next_meeting_type', label: 'Next meeting type' },
  { key: 'next_meeting_start', label: 'Next meeting start' },
  { key: 'next_meeting_scheduled', label: 'Next meeting scheduled' },
  { key: 'invitation_status', label: 'Invitation status' },
  { key: 'finish_invitations', label: 'Finish invitations' },
  { key: 'done_meetings', label: 'Done meetings' },
  { key: 'active_meetings', label: 'Active meetings' },
  { key: 'noshow_meetings', label: 'No-show meetings' },
  { key: 'canceled_meetings', label: 'Canceled meetings' },
];

// Fetched live from the Taliaz agent API on expand (not in BigQuery) — see
// backend/app/services/taliaz_agent_api.py. last_meeting_summary is rendered
// separately below the grid since it's long-form text.
const EXTRA_FIELDS: { key: Exclude<keyof PatientExtraDetails, 'meeting_summaries_history'>; label: string }[] = [
  { key: 'health_fund', label: 'Health fund' },
  { key: 'invite_link', label: 'Invite link' },
  { key: 'future_meeting_type', label: 'Future meeting type' },
  { key: 'future_meeting_date', label: 'Future meeting date' },
  { key: 'future_yoman_link', label: 'Future meeting link' },
  { key: 'meeting1_type', label: 'Last meeting 1 type' },
  { key: 'meeting1_date', label: 'Last meeting 1 date' },
  { key: 'meeting2_type', label: 'Last meeting 2 type' },
  { key: 'meeting2_date', label: 'Last meeting 2 date' },
  { key: 'meeting3_type', label: 'Last meeting 3 type' },
  { key: 'meeting3_date', label: 'Last meeting 3 date' },
  { key: 'meeting1_case_manager_name', label: 'Meeting 1 case manager' },
  { key: 'meeting1_doctor_name', label: 'Meeting 1 doctor' },
  { key: 'meeting1_diagnosis', label: 'Meeting 1 diagnosis' },
  { key: 'meeting1_prescription', label: 'Meeting 1 prescription' },
  { key: 'meeting1_team_url', label: 'Meeting 1 Teams link' },
];

function formatValue(value: string | number | boolean | null | undefined): string {
  if (value === null || value === undefined || value === '') return '—';
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  return String(value);
}

// next_meeting_type/next_meeting_start don't get cleared once the meeting is
// no longer upcoming — BigQuery just leaves the last value in place, so a
// patient with next_meeting_scheduled=false can still show a (past) date
// there. next_meeting_scheduled is the authoritative flag; treat the other
// two as unset whenever it's false so the table doesn't imply a meeting that
// doesn't exist.
function getDetailValue(p: Patient, key: keyof Patient): Patient[keyof Patient] {
  if ((key === 'next_meeting_type' || key === 'next_meeting_start') && !p.next_meeting_scheduled) {
    return null;
  }
  return p[key];
}

type ExtraState = { status: 'loading' } | { status: 'error' } | { status: 'ready'; data: PatientExtraDetails };

export default function Patients() {
  const [patients, setPatients] = useState<Patient[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [expandedId, setExpandedId] = useState<number | null>(null);
  // Keyed by internal_id, fetched lazily on first expand and cached — the
  // agent API is per-patient and shouldn't be called for the whole list.
  const [extra, setExtra] = useState<Record<number, ExtraState>>({});

  useEffect(() => {
    patientsApi
      .list()
      .then((res) => setPatients(res.data.patients))
      .catch((err) => setError(err.response?.data?.detail || err.message || 'Failed to load patients'))
      .finally(() => setLoading(false));
  }, []);

  const toggleExpand = (internalId: number) => {
    if (expandedId === internalId) {
      setExpandedId(null);
      return;
    }
    setExpandedId(internalId);
    if (!extra[internalId]) {
      setExtra((prev) => ({ ...prev, [internalId]: { status: 'loading' } }));
      patientsApi
        .getExtraDetails(internalId)
        .then((res) => setExtra((prev) => ({ ...prev, [internalId]: { status: 'ready', data: res.data } })))
        .catch(() => setExtra((prev) => ({ ...prev, [internalId]: { status: 'error' } })));
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <main className="mx-auto max-w-6xl p-6">
        <Card>
          <div className="flex items-center justify-between">
            <Title>Patients</Title>
            {!loading && !error && (
              <Text>{patients.length} records</Text>
            )}
          </div>

          {loading && <Text className="mt-6">Loading…</Text>}
          {error && <Text className="mt-6 text-red-500">{error}</Text>}

          {!loading && !error && (
            <Table className="mt-6">
              <TableHead>
                <TableRow>
                  <TableHeaderCell />
                  <TableHeaderCell>ID</TableHeaderCell>
                  <TableHeaderCell>Name</TableHeaderCell>
                  <TableHeaderCell>Clinic</TableHeaderCell>
                  <TableHeaderCell>Status</TableHeaderCell>
                  <TableHeaderCell>Created</TableHeaderCell>
                  <TableHeaderCell className="text-right">Waiting (days)</TableHeaderCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {patients.map((p) => {
                  const isExpanded = expandedId === p.internal_id;
                  const extraState = extra[p.internal_id];
                  const extraData = extraState?.status === 'ready' ? extraState.data : null;
                  return (
                    <React.Fragment key={p.internal_id}>
                      <TableRow
                        className="cursor-pointer hover:bg-gray-100"
                        onClick={() => toggleExpand(p.internal_id)}
                      >
                        <TableCell className="w-4 text-gray-400">{isExpanded ? '▾' : '▸'}</TableCell>
                        <TableCell>{p.hm_id || p.internal_id}</TableCell>
                        <TableCell>
                          {[p.first_name, p.last_name].filter(Boolean).join(' ') || '—'}
                        </TableCell>
                        <TableCell>{p.clinic_name || p.clinic_group || '—'}</TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            {p.status ? <Badge>{p.status}</Badge> : '—'}
                            {p.red_flag && <Badge color="red">Red flag</Badge>}
                          </div>
                        </TableCell>
                        <TableCell>{p.created_at || '—'}</TableCell>
                        <TableCell className="text-right">{p.waiting_time}</TableCell>
                      </TableRow>
                      {isExpanded && (
                        <TableRow>
                          <TableCell colSpan={7} className="bg-gray-50">
                            <div className="grid grid-cols-2 gap-x-8 gap-y-2 py-2 sm:grid-cols-3 md:grid-cols-4">
                              {DETAIL_FIELDS.map(({ key, label }) => (
                                <div key={key}>
                                  <Text className="text-xs text-gray-400">{label}</Text>
                                  <Text className="whitespace-normal break-all text-sm">{formatValue(getDetailValue(p, key))}</Text>
                                </div>
                              ))}
                            </div>

                            <div className="mt-4 border-t border-gray-200 pt-4">
                              <Text className="text-xs font-medium uppercase tracking-wide text-gray-400">
                                Live data (Taliaz agent API)
                              </Text>
                              {extraState?.status === 'loading' && (
                                <Text className="mt-2 text-sm text-gray-400">Loading…</Text>
                              )}
                              {extraState?.status === 'error' && (
                                <Text className="mt-2 text-sm text-red-500">Failed to load live data</Text>
                              )}
                              {extraData && (
                                <>
                                  <div className="mt-2 grid grid-cols-2 gap-x-8 gap-y-2 sm:grid-cols-3 md:grid-cols-4">
                                    {EXTRA_FIELDS.map(({ key, label }) => (
                                      <div key={key}>
                                        <Text className="text-xs text-gray-400">{label}</Text>
                                        <Text className="whitespace-normal break-all text-sm">{formatValue(extraData[key])}</Text>
                                      </div>
                                    ))}
                                  </div>
                                  <div className="mt-3">
                                    <Text className="text-xs text-gray-400">Last meeting summary</Text>
                                    <Text className="mt-1 whitespace-pre-wrap text-sm">
                                      {formatValue(extraData.last_meeting_summary)}
                                    </Text>
                                  </div>
                                  {extraData.meeting_summaries_history.length > 0 && (
                                    <div className="mt-3">
                                      <Text className="text-xs text-gray-400">Meeting summaries history</Text>
                                      <div className="mt-1 space-y-3">
                                        {extraData.meeting_summaries_history.map((entry, i) => (
                                          <div key={i} className="rounded border border-gray-200 p-2">
                                            <Text className="text-xs font-medium text-gray-500">
                                              {entry.name || `Entry ${i + 1}`}
                                            </Text>
                                            <Text className="mt-1 whitespace-pre-wrap text-sm">
                                              {formatValue(entry.summary)}
                                            </Text>
                                          </div>
                                        ))}
                                      </div>
                                    </div>
                                  )}
                                </>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      )}
                    </React.Fragment>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </Card>
      </main>
    </div>
  );
}
